import { Link, useLocation } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { OrganizationSwitcher } from '@/components/organization/OrganizationSwitcher';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { theme } from '@/lib/ui/theme';

interface NavigationProps {
  isLoading?: boolean;
  isSignedIn?: boolean; // Added isSignedIn prop
  variant?: 'transparent' | 'solid';
  mode?: 'marketing' | 'dashboard';
  showAdminNav?: boolean;
  onSignOut?: () => void | Promise<void>;
}

// Cycled to give the nav skeleton varied widths; one placeholder per real link.
const SKELETON_WIDTH_POOL = ['w-20', 'w-14', 'w-10', 'w-12', 'w-16'];

export function Navigation({
  isLoading = false,
  isSignedIn = false,
  variant = 'transparent',
  mode = 'marketing',
  showAdminNav = false,
  onSignOut,
}: NavigationProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const dashboardLinks = [
    { label: 'Get Started', href: '/getting-started' },
    { label: 'Docs', href: '/docs' },
    { label: 'Contact', href: '/contact' },
  ];
  const staffLinks = [
    { label: 'Review', href: '/review' },
    { label: 'Tasks', href: '/tasks' },
    { label: 'Telemetry', href: '/telemetry' },
  ];

  const navLinks =
    mode === 'dashboard'
      ? dashboardLinks
      : [
          { label: 'Get Started', href: '/getting-started' },
          { label: 'Pricing', href: '/pricing' },
          { label: 'FAQ', href: '/faq' },
          { label: 'Docs', href: '/docs' },
          { label: 'Philosophy', href: '/philosophy' },
          { label: 'Consulting', href: '/consulting' },
          { label: 'Contact', href: '/contact' },
        ];

  // One skeleton placeholder per real nav link, derived so the count can't
  // drift from navLinks when links are added/removed.
  const navSkeletonWidths = Array.from(
    { length: navLinks.length + (showAdminNav ? 1 : 0) },
    (_, i) => SKELETON_WIDTH_POOL[i % SKELETON_WIDTH_POOL.length]
  );

  // Wait for theme to mount to avoid hydration mismatch
  // Use Tailwind CSS variables (bg-background, border-border) which are theme-aware
  if (!mounted) {
    return (
      <nav className="bg-background/80 border-border fixed top-0 z-50 w-full border-b px-6 py-3 backdrop-blur-md">
        <div
          className="mx-auto flex items-center justify-between md:grid md:grid-cols-[1fr_auto_1fr]"
          style={{ maxWidth: theme.spacing.maxWidth }}
        >
          <div className="flex items-center gap-3 md:justify-self-start">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <Skeleton className="h-6 w-28" />
          </div>
          <div className="hidden items-center gap-8 md:flex md:justify-self-center">
            {navSkeletonWidths.map((w, i) => (
              <Skeleton key={i} className={`h-4 ${w}`} />
            ))}
          </div>
          <Skeleton className="h-9 w-20 rounded-md md:justify-self-end" />
        </div>
      </nav>
    );
  }

  const isDark = resolvedTheme === 'dark';

  if (isLoading) {
    return (
      <nav
        className="fixed top-0 z-50 w-full px-6 py-3"
        style={{
          backdropFilter: theme.glass.card.backdropBlur,
          backgroundColor:
            variant === 'transparent'
              ? theme.backgrounds.navigation.transparent[
                  isDark ? 'dark' : 'light'
                ]
              : theme.backgrounds.page[isDark ? 'dark' : 'light'],
          borderBottom: `1px solid ${theme.glass.card.border[isDark ? 'dark' : 'light']}`,
        }}
      >
        <div
          className="mx-auto flex items-center justify-between md:grid md:grid-cols-[1fr_auto_1fr]"
          style={{ maxWidth: theme.spacing.maxWidth }}
        >
          <div className="flex items-center gap-3 md:justify-self-start">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <Skeleton className="h-6 w-28" />
          </div>
          <div className="hidden items-center gap-8 md:flex md:justify-self-center">
            {navSkeletonWidths.map((w, i) => (
              <Skeleton key={i} className={`h-4 ${w}`} />
            ))}
          </div>
          {/* Sign Out stays real while loading, never a skeleton. A session that cannot resolve
              (no active organization, a rejected switch) otherwise leaves the user on a page with
              no controls at all and no way back out — the failure this nav is the last line
              against. Same width as the loaded button, so nothing shifts. */}
          {mode === 'dashboard' ? (
            <div className="hidden items-center gap-3 md:flex md:justify-self-end">
              <OrganizationSwitcher />
              <Button
                variant="secondary"
                className="transition-all duration-200 hover:!opacity-100 hover:!brightness-90 dark:hover:!brightness-150"
                onClick={onSignOut}
              >
                Sign Out
              </Button>
            </div>
          ) : (
            <Skeleton className="hidden h-9 w-20 rounded-md md:block md:justify-self-end" />
          )}
          {/* The mobile hamburger is a skeleton here and the mobile menu (which holds Sign Out)
              only renders in the loaded nav, so a phone user would have no control at all.
              Give dashboard mode a direct Sign Out instead of the placeholder. */}
          {mode === 'dashboard' ? (
            <Button
              variant="secondary"
              className="md:hidden"
              onClick={onSignOut}
            >
              Sign Out
            </Button>
          ) : (
            <Skeleton className="h-8 w-8 md:hidden" />
          )}
        </div>
      </nav>
    );
  }

  return (
    <nav
      className="fixed top-0 z-50 w-full px-6 py-3"
      style={{
        backdropFilter: theme.glass.card.backdropBlur,
        backgroundColor:
          variant === 'transparent'
            ? theme.backgrounds.navigation.transparent[
                isDark ? 'dark' : 'light'
              ]
            : theme.backgrounds.page[isDark ? 'dark' : 'light'],
        borderBottom: `1px solid ${theme.glass.card.border[isDark ? 'dark' : 'light']}`,
      }}
    >
      <div
        className="mx-auto flex items-center justify-between md:grid md:grid-cols-[1fr_auto_1fr]"
        style={{ maxWidth: theme.spacing.maxWidth }}
      >
        {/* Left: Logo + Mindrealm text */}
        <Link
          to="/"
          search={{}}
          className="flex items-center gap-3 transition-all duration-200 hover:brightness-110 md:justify-self-start"
        >
          <div
            className="flex h-10 w-10 items-center justify-center rounded-lg text-2xl"
            style={{
              background: '#5b21b6',
              fontFamily: theme.fonts.display,
              textShadow: '2px 3px 4px rgba(0, 0, 0, 0.5)',
            }}
          >
            🪲
          </div>
          <span
            className="text-gradient-hero text-2xl font-bold"
            style={{
              fontFamily: theme.fonts.display,
            }}
          >
            Mindrealm
          </span>
        </Link>

        {/* Center: Navigation links (hidden on mobile) */}
        <div className="hidden items-center gap-8 md:flex md:justify-self-center">
          {navLinks.map((link) => {
            const isActive = pathname === link.href;
            const linkStyle = {
              fontFamily: theme.fonts.body,
              fontSize: theme.typography.scale.body.fontSize,
              fontWeight: isActive ? '700' : '500',
              color: theme.colors.text.primary[isDark ? 'dark' : 'light'],
              textDecoration: isActive ? 'underline' : 'none',
              textDecorationColor: isActive
                ? theme.focusRings.primary.color
                : 'transparent',
              textUnderlineOffset: '4px',
            };
            return (
              <Link
                key={link.href}
                to={link.href}
                className="transition-opacity hover:opacity-70"
                style={linkStyle}
              >
                {link.label}
              </Link>
            );
          })}
          {showAdminNav && (
            <details className="group relative">
              <summary
                className="flex cursor-pointer list-none items-center gap-1 transition-opacity hover:opacity-70"
                style={{
                  fontFamily: theme.fonts.body,
                  fontSize: theme.typography.scale.body.fontSize,
                  fontWeight: staffLinks.some((link) => pathname === link.href)
                    ? '700'
                    : '500',
                  color: theme.colors.text.primary[isDark ? 'dark' : 'light'],
                }}
              >
                Admin
                <ChevronDown
                  className="h-4 w-4 transition-transform group-open:rotate-180"
                  aria-hidden="true"
                />
              </summary>
              <div className="border-border bg-background absolute top-full right-0 z-50 mt-3 min-w-36 space-y-1 rounded-md border p-2 shadow-lg">
                {staffLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="hover:bg-muted block rounded px-3 py-2 text-sm"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Right: Sign In/Out button */}
        <div className="hidden items-center gap-3 md:flex md:justify-self-end">
          {mode === 'dashboard' ? (
            <>
              <OrganizationSwitcher />
              <Button
                variant="secondary"
                className="transition-all duration-200 hover:!opacity-100 hover:!brightness-90 dark:hover:!brightness-150"
                onClick={onSignOut}
              >
                Sign Out
              </Button>
            </>
          ) : isSignedIn ? (
            <Button
              variant="secondary"
              className="transition-all duration-200 hover:!opacity-100 hover:!brightness-90 dark:hover:!brightness-150"
              asChild
            >
              <Link to="/dashboard">Dashboard</Link>
            </Button>
          ) : (
            <Button
              variant="secondary"
              className="transition-all duration-200 hover:!opacity-100 hover:!brightness-90 dark:hover:!brightness-150"
              asChild
            >
              <Link to="/sign-in" search={{}}>
                Sign In
              </Link>
            </Button>
          )}
        </div>

        {/* Mobile: Hamburger menu button */}
        <button
          className="flex flex-col gap-1.5 md:hidden"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle mobile menu"
          style={{
            padding: '0.5rem',
          }}
        >
          <span
            className="h-0.5 w-6 transition-all"
            style={{
              backgroundColor:
                theme.colors.text.primary[isDark ? 'dark' : 'light'],
              transform: isMobileMenuOpen
                ? 'rotate(45deg) translateY(8px)'
                : 'none',
            }}
          />
          <span
            className="h-0.5 w-6 transition-all"
            style={{
              backgroundColor:
                theme.colors.text.primary[isDark ? 'dark' : 'light'],
              opacity: isMobileMenuOpen ? 0 : 1,
            }}
          />
          <span
            className="h-0.5 w-6 transition-all"
            style={{
              backgroundColor:
                theme.colors.text.primary[isDark ? 'dark' : 'light'],
              transform: isMobileMenuOpen
                ? 'rotate(-45deg) translateY(-8px)'
                : 'none',
            }}
          />
        </button>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="mt-4 flex flex-col gap-4 border-t pt-4 md:hidden"
          style={{
            borderColor: theme.glass.card.border[isDark ? 'dark' : 'light'],
          }}
        >
          {navLinks.map((link) => {
            const isActive = pathname === link.href;
            const mobileLinkStyle = {
              fontFamily: theme.fonts.body,
              fontSize: theme.typography.scale.body.fontSize,
              fontWeight: isActive ? '700' : '500',
              color: isActive
                ? theme.focusRings.primary.color
                : theme.colors.text.primary[isDark ? 'dark' : 'light'],
            };
            return (
              <Link
                key={link.href}
                to={link.href}
                className="transition-opacity hover:opacity-70"
                style={mobileLinkStyle}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            );
          })}
          {showAdminNav && (
            <section
              aria-label="Admin"
              className="border-border space-y-3 border-t pt-4"
            >
              <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                Admin
              </p>
              {staffLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="block transition-opacity hover:opacity-70"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </section>
          )}
          <div className="flex flex-col gap-3">
            {mode === 'dashboard' ? (
              <Button
                variant="secondary"
                className="transition-all duration-200 hover:!opacity-100 hover:!brightness-90 dark:hover:!brightness-150"
                onClick={onSignOut}
              >
                Sign Out
              </Button>
            ) : isSignedIn ? (
              <Button
                variant="secondary"
                className="transition-all duration-200 hover:!opacity-100 hover:!brightness-90 dark:hover:!brightness-150"
                asChild
              >
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            ) : (
              <Button
                variant="secondary"
                className="transition-all duration-200 hover:!opacity-100 hover:!brightness-90 dark:hover:!brightness-150"
                asChild
              >
                <Link to="/sign-in" search={{}}>
                  Sign In
                </Link>
              </Button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
